import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { adminContactInquiriesAPI } from '../../services/api';

const STATUS = {
  new:       { bg: 'rgba(0,212,170,0.12)', fg: '#00d4aa', label: 'New' },
  contacted: { bg: 'rgba(99,102,241,0.12)', fg: '#818cf8', label: 'Contacted' },
  closed:    { bg: 'rgba(148,163,184,0.12)', fg: '#94a3b8', label: 'Closed' },
};

const fmt = (s) => s ? new Date(s).toLocaleString('en-IN', {
  day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true,
}) : '—';

export default function AdminContactInquiries({ onBack, embedded = false }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedId, setSelectedId] = useState(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    const r = await adminContactInquiriesAPI.list();
    if (r.error) setError(r.error);
    else setItems(r.data?.inquiries || []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const list = statusFilter === 'all' ? items : items.filter(i => i.status === statusFilter);
    return [...list].sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
  }, [items, statusFilter]);

  const selected = useMemo(
    () => items.find(i => i.inquiryId === selectedId) || null,
    [items, selectedId]
  );

  useEffect(() => {
    setAdminNotes(selected?.adminNotes || '');
  }, [selected]);

  const counts = useMemo(() => ({
    new: items.filter(i => i.status === 'new').length,
    contacted: items.filter(i => i.status === 'contacted').length,
    closed: items.filter(i => i.status === 'closed').length,
  }), [items]);

  const updateStatus = async (status) => {
    if (!selected) return;
    setSaving(true);
    const r = await adminContactInquiriesAPI.update(selected.inquiryId, { status, adminNotes: adminNotes.trim() });
    setSaving(false);
    if (r.error) { setError(r.error); return; }
    setItems(p => p.map(i => i.inquiryId === selected.inquiryId
      ? { ...i, status, adminNotes: adminNotes.trim(), updatedAt: new Date().toISOString() }
      : i));
  };

  return (
    <div style={{ padding: embedded ? 0 : 24 }}>
      {!embedded && (
        <button type="button" onClick={onBack} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', marginBottom: 16 }}>
          ← Back
        </button>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 18 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 800, margin: 0 }}>Get in Touch — Hospital Leads</h1>
          <p style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 6 }}>
            Inquiries from the login page contact form. Call or email hospitals from here.
          </p>
        </div>
        <button type="button" onClick={load} disabled={loading}
          style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid var(--border-color)', background: 'var(--surface-subtle)', color: 'var(--text-secondary)', cursor: 'pointer', fontWeight: 600 }}>
          {loading ? 'Refreshing…' : '↻ Refresh'}
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 10, marginBottom: 16 }}>
        {[
          { k: 'new', label: 'New', c: '#00d4aa' },
          { k: 'contacted', label: 'Contacted', c: '#818cf8' },
          { k: 'closed', label: 'Closed', c: '#94a3b8' },
        ].map(({ k, label, c }) => (
          <div key={k} style={{ padding: '12px 14px', borderRadius: 12, background: `${c}14`, border: `1px solid ${c}33` }}>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</div>
            <div style={{ fontSize: 24, fontWeight: 800, color: c }}>{counts[k]}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
        {['all', 'new', 'contacted', 'closed'].map(s => (
          <button key={s} type="button" onClick={() => setStatusFilter(s)}
            style={{
              padding: '6px 12px', borderRadius: 20, border: '1px solid var(--border-color)',
              background: statusFilter === s ? 'rgba(0,212,170,0.12)' : 'transparent',
              color: statusFilter === s ? '#00d4aa' : 'var(--text-muted)', cursor: 'pointer', fontWeight: 600, fontSize: 12,
            }}>
            {s === 'all' ? 'All' : STATUS[s]?.label || s}
          </button>
        ))}
      </div>

      {error && (
        <div style={{ padding: 12, marginBottom: 14, borderRadius: 10, background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)', color: '#f87171', fontSize: 13 }}>
          {error}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(280px, 360px) 1fr', gap: 14, minHeight: 420 }}>
        <div style={{ border: '1px solid var(--border-color)', borderRadius: 12, overflow: 'auto', maxHeight: 560 }}>
          {loading && !items.length ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Loading…</div>
          ) : !filtered.length ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>No inquiries yet</div>
          ) : filtered.map(item => {
            const st = STATUS[item.status] || STATUS.new;
            const active = selectedId === item.inquiryId;
            return (
              <button key={item.inquiryId} type="button" onClick={() => setSelectedId(item.inquiryId)}
                style={{
                  width: '100%', textAlign: 'left', padding: '12px 14px', border: 'none', borderBottom: '1px solid var(--border-color)',
                  background: active ? 'rgba(0,212,170,0.08)' : 'transparent', cursor: 'pointer',
                }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center' }}>
                  <span style={{ fontWeight: 700, fontSize: 13, color: 'var(--text-primary)' }}>{item.hospitalName || 'Hospital'}</span>
                  <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20, background: st.bg, color: st.fg }}>{st.label}</span>
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>{item.contactName}</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>{fmt(item.createdAt)}</div>
              </button>
            );
          })}
        </div>

        <div style={{ border: '1px solid var(--border-color)', borderRadius: 12, padding: 18 }}>
          {!selected ? (
            <div style={{ color: 'var(--text-muted)', textAlign: 'center', paddingTop: 80 }}>Select an inquiry to view details</div>
          ) : (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
                <div>
                  <div style={{ fontSize: 18, fontWeight: 800 }}>{selected.hospitalName}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>{selected.inquiryId} · {fmt(selected.createdAt)}</div>
                </div>
                <span style={{ fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 20, background: (STATUS[selected.status] || STATUS.new).bg, color: (STATUS[selected.status] || STATUS.new).fg, height: 'fit-content' }}>
                  {(STATUS[selected.status] || STATUS.new).label}
                </span>
              </div>

              <div style={{ display: 'grid', gap: 10, fontSize: 13, marginBottom: 16 }}>
                <div><strong>Contact:</strong> {selected.contactName}</div>
                <div><strong>Mobile:</strong> <a href={`tel:${selected.mobile}`} style={{ color: '#60a5fa' }}>{selected.mobile}</a></div>
                <div><strong>Email:</strong> <a href={`mailto:${selected.email}`} style={{ color: '#60a5fa' }}>{selected.email}</a></div>
                <div><strong>Address:</strong> {selected.address}</div>
                {selected.description && (
                  <div style={{ padding: 12, borderRadius: 10, background: 'var(--surface-subtle)', lineHeight: 1.55 }}>
                    <strong>Message</strong><br />{selected.description}
                  </div>
                )}
              </div>

              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 6, color: 'var(--text-secondary)' }}>Internal notes</label>
              <textarea value={adminNotes} onChange={e => setAdminNotes(e.target.value)} rows={3}
                placeholder="Call notes, follow-up date, etc."
                style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid var(--border-color)', background: 'var(--surface-subtle)', color: 'var(--text-primary)', fontSize: 13, marginBottom: 12, resize: 'vertical' }} />

              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {selected.status !== 'contacted' && (
                  <button type="button" disabled={saving} onClick={() => updateStatus('contacted')}
                    style={{ padding: '8px 14px', borderRadius: 8, border: 'none', background: '#6366f1', color: '#fff', fontWeight: 700, cursor: 'pointer' }}>
                    Mark Contacted
                  </button>
                )}
                {selected.status !== 'closed' && (
                  <button type="button" disabled={saving} onClick={() => updateStatus('closed')}
                    style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid var(--border-color)', background: 'transparent', color: 'var(--text-secondary)', fontWeight: 600, cursor: 'pointer' }}>
                    Close
                  </button>
                )}
                {selected.status !== 'new' && (
                  <button type="button" disabled={saving} onClick={() => updateStatus('new')}
                    style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid var(--border-color)', background: 'transparent', color: '#00d4aa', fontWeight: 600, cursor: 'pointer' }}>
                    Re-open as New
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
